/**
 * Grouping helpers for the flow action dropdown (`#flow-action-select` in EditStep.vue).
 *
 * Connector action descriptions usually carry the OpenAPI tag as a prefix — "Issues: Create issue
 * (POST)" — so the tag can be turned into a group header at no payload cost: the key is already
 * inside the description the summary endpoint sends.
 *
 * Two properties of the real catalog shape this module:
 *
 * 1. Groups are already contiguous in source order (measured: group count equals block count on
 *    teams 34, jira 87, orangelogic 175, impartner 355). So grouping is a single pass over the
 *    rendered window, not a sort — the connector's own ordering survives.
 * 2. Grouping is not always a win. adobeCommerce yields 423 groups for 526 actions and pimcore
 *    has no prefixes at all, so a threshold decides whether to group (see
 *    {@link computeGroupingViability}); when it fails the caller renders a flat list, which is
 *    the previous behaviour.
 *
 * Pure functions only, no Vue/DOM, so the behaviour is unit testable in isolation.
 */

// Tag and text are separated by a colon *and* a space. Requiring the space avoids splitting
// descriptions that merely contain a colon, e.g. a URL or a time.
const GROUP_SEPARATOR = ": ";

export interface GroupableAction {
  action?: { actionSpec?: { description?: string | null } | null } | null;
}

export interface ActionGroup<T> {
  /** Null for the bucket holding actions whose description carries no tag. */
  key: string | null;
  label: string;
  items: T[];
}

export interface GroupingThresholds {
  minPrefixRatio: number;
  minPerGroup: number;
}

export interface ActionGroupSummary {
  /** Null for the bucket holding actions whose description carries no tag. */
  key: string | null;
  label: string;
  count: number;
}

/**
 * The tag in front of a description, or null when there is none. Both sides must be non-empty:
 * a description that merely starts or ends with the separator is not tagged.
 */
export function deriveGroupKey(description: unknown): string | null {
  if (typeof description !== "string") {
    return null;
  }
  const at = description.indexOf(GROUP_SEPARATOR);
  if (at <= 0) {
    return null;
  }
  const key = description.slice(0, at).trim();
  const rest = description.slice(at + GROUP_SEPARATOR.length).trim();
  return key && rest ? key : null;
}

/**
 * The description without its tag, for rendering under a group header. Returns the description
 * unchanged when it carries no tag, so an untagged action never loses text.
 */
export function stripGroupPrefix(description: unknown): string {
  if (typeof description !== "string") {
    return "";
  }
  if (deriveGroupKey(description) === null) {
    return description;
  }
  return description
    .slice(description.indexOf(GROUP_SEPARATOR) + GROUP_SEPARATOR.length)
    .trim();
}

function descriptionOf(entry: GroupableAction): string | null {
  const description = entry?.action?.actionSpec?.description;
  return typeof description === "string" ? description : null;
}

/**
 * Whether grouping this connector's actions would actually help.
 *
 * Two conditions, both measured against the whole list:
 * - enough actions carry a tag at all (pimcore has none);
 * - the headers meaningfully compress the list (adobeCommerce would show 423 headers over 526
 *   rows, which reads worse than a flat list).
 *
 * The divisor is the number of headers the user would actually see, so the untagged bucket counts
 * as one of them.
 */
export function computeGroupingViability(
  entries: GroupableAction[],
  { minPrefixRatio, minPerGroup }: GroupingThresholds
): boolean {
  if (!Array.isArray(entries) || entries.length === 0) {
    return false;
  }

  const keys = new Set<string>();
  let tagged = 0;
  entries.forEach((entry) => {
    const key = deriveGroupKey(descriptionOf(entry));
    if (key !== null) {
      tagged += 1;
      keys.add(key);
    }
  });

  if (keys.size === 0 || tagged / entries.length < minPrefixRatio) {
    return false;
  }

  const headerCount = keys.size + (tagged < entries.length ? 1 : 0);
  return entries.length / headerCount >= minPerGroup;
}

/**
 * Moves untagged actions to the front, keeping relative order on both sides.
 *
 * They are hoisted rather than left in place because they are scattered in practice (openai's four
 * untagged actions sit at source positions 34, 35, 56 and 81), which would otherwise produce
 * several separate headerless blocks between the tagged ones. Hoisting also keeps them reachable:
 * with a render window that grows on scroll, "at the end" can mean never seen.
 */
export function orderUngroupedFirst<T extends GroupableAction>(
  entries: T[]
): T[] {
  if (!Array.isArray(entries)) {
    return [];
  }
  const ungrouped: T[] = [];
  const grouped: T[] = [];
  entries.forEach((entry) => {
    if (deriveGroupKey(descriptionOf(entry)) === null) {
      ungrouped.push(entry);
    } else {
      grouped.push(entry);
    }
  });
  return ungrouped.length === 0 ? entries : [...ungrouped, ...grouped];
}

/**
 * Splits an already-ordered list into consecutive groups. Relies on the caller having ordered the
 * entries (see {@link orderUngroupedFirst}); a key that reappears after a different one simply
 * starts another group rather than merging, which preserves source order over tidiness.
 */
export function buildActionGroups<T extends GroupableAction>(
  entries: T[],
  ungroupedLabel: string
): ActionGroup<T>[] {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  const groups: ActionGroup<T>[] = [];
  entries.forEach((entry) => {
    const key = deriveGroupKey(descriptionOf(entry));
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(entry);
      return;
    }
    groups.push({
      key,
      label: key ?? ungroupedLabel,
      items: [entry],
    });
  });
  return groups;
}

/**
 * One row per distinct tag, for a group selector that filters the action list instead of heading
 * it. Order comes from {@link orderUngroupedFirst} and {@link buildActionGroups} so the selector
 * lists groups exactly as the inline headers would, untagged bucket first.
 *
 * The one difference: a tag that reappears later is merged here rather than kept as a second row.
 * Selecting a row filters by key, so two rows for one key would be two identical filters.
 */
export function summariseGroups<T extends GroupableAction>(
  entries: T[],
  ungroupedLabel: string
): ActionGroupSummary[] {
  const summaries: ActionGroupSummary[] = [];
  const byKey = new Map<string | null, ActionGroupSummary>();

  buildActionGroups(orderUngroupedFirst(entries), ungroupedLabel).forEach(
    (group) => {
      const seen = byKey.get(group.key);
      if (seen) {
        seen.count += group.items.length;
        return;
      }
      const summary = {
        key: group.key,
        label: group.label,
        count: group.items.length,
      };
      byKey.set(group.key, summary);
      summaries.push(summary);
    }
  );

  return summaries;
}
